import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProducts } from '../context/ProductsContext';
import Footer from '../components/Footer';

const AccessoriesPage: React.FC = () => {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
  const navigate = useNavigate();
  const { products: apiProducts } = useProducts();
  const [guideImage, setGuideImage] = useState<string>('');
  const [ezCareCarousel, setEzCareCarousel] = useState<string[]>([]);
  const [ezCarePlusCarousel, setEzCarePlusCarousel] = useState<string[]>([]);
  const [lifeBeaconCarousel, setLifeBeaconCarousel] = useState<string[]>([]);
  const [hoveredProduct, setHoveredProduct] = useState<number | null>(null);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState<{ [key: number]: number }>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [accessoryImages, setAccessoryImages] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    // Fetch all images
    fetch(`${API_URL}/api/images`)
      .then(res => res.json())
      .then((data: any) => {
        // Guide image
        const guideImg = data.images.find((img: any) => 
          img.filename.toLowerCase().includes('ladys-tea') ||
          img.filename.toLowerCase().includes('ladystea') ||
          img.filename.toLowerCase().includes('ladies-tea')
        );
        if (guideImg) {
          setGuideImage(`${API_URL}/api/images/${guideImg.id}`);
        }

        // EZ Care carousel images
        const ezCareCarouselImgs = data.images
          .filter((img: any) => 
            img.filename.toLowerCase().includes('ez-care-in-home') &&
            img.filename.toLowerCase().includes('carousel') &&
            !img.filename.toLowerCase().includes('plus')
          )
          .map((img: any) => `${API_URL}/api/images/${img.id}`);
        setEzCareCarousel(ezCareCarouselImgs);

        // EZ Care Plus carousel images
        const ezCarePlusCarouselImgs = data.images
          .filter((img: any) => 
            img.filename.toLowerCase().includes('ez-care-plus') &&
            img.filename.toLowerCase().includes('carousel')
          )
          .map((img: any) => `${API_URL}/api/images/${img.id}`);
        setEzCarePlusCarousel(ezCarePlusCarouselImgs);

        // LifeBeacon carousel images
        const lifeBeaconCarouselImgs = data.images
          .filter((img: any) => 
            img.filename.toLowerCase().includes('lifebeacon') &&
            img.filename.toLowerCase().includes('carousel')
          )
          .map((img: any) => `${API_URL}/api/images/${img.id}`);
        setLifeBeaconCarousel(lifeBeaconCarouselImgs);

        // Accessory images - match by exact filenames
        const imageMap: { [key: string]: string } = {};
        
        // Added Protection Plan
        const protectionImg = data.images.find((img: any) => 
          img.filename === 'alert1AddedProtectionPlan.jpg'
        );
        if (protectionImg) imageMap['protection'] = `${API_URL}/api/images/${protectionImg.id}`;

        // Smoke Detector
        const smokeImg = data.images.find((img: any) => 
          img.filename === 'accessories-Smoke-Detector.jpg'
        );
        if (smokeImg) imageMap['smoke'] = `${API_URL}/api/images/${smokeImg.id}`;

        // Medical Alert Necklace with Help Button
        const necklaceHelpImg = data.images.find((img: any) => 
          img.filename === 'Pendant-med.jpg'
        );
        if (necklaceHelpImg) imageMap['necklace-help'] = `${API_URL}/api/images/${necklaceHelpImg.id}`;

        // Emergency Lock Box
        const lockBoxImg = data.images.find((img: any) => 
          img.filename === 'accessories-Lock-Box.jpg'
        );
        if (lockBoxImg) imageMap['lockbox'] = `${API_URL}/api/images/${lockBoxImg.id}`;

        // Medical Alert Necklace with Fall Detection
        const necklaceFallImg = data.images.find((img: any) => 
          img.filename === 'accessories-Pendant.jpg'
        );
        if (necklaceFallImg) imageMap['necklace-fall'] = `${API_URL}/api/images/${necklaceFallImg.id}`;

        // Mobile Alert Pendant with GPS - use same image as LifeBeacon
        const mobileGPSImg = data.images.find((img: any) => 
          img.filename.toLowerCase() === 'lifebeacon-on-the-go-1.jpg'
        );
        if (mobileGPSImg) imageMap['mobile-gps'] = `${API_URL}/api/images/${mobileGPSImg.id}`;

        // Mobile Fall Detection Alert Pendant with GPS - use same image as LifeBeacon
        const mobileFallGPSImg = data.images.find((img: any) => 
          img.filename.toLowerCase() === 'lifebeacon-on-the-go-1.jpg'
        );
        if (mobileFallGPSImg) imageMap['mobile-fall-gps'] = `${API_URL}/api/images/${mobileFallGPSImg.id}`;

        // Emergency Alert Bracelet
        const braceletImg = data.images.find((img: any) => 
          img.filename === 'accessories-wristband.png'
        );
        if (braceletImg) imageMap['bracelet'] = `${API_URL}/api/images/${braceletImg.id}`;

        // Medication Reminder
        const medicationImg = data.images.find((img: any) => 
          img.filename === 'MedReady-Medication-Reminder-Organizer.-alert1-medical-alert-system-2-.jpg'
        );
        if (medicationImg) imageMap['medication'] = `${API_URL}/api/images/${medicationImg.id}`;

        setAccessoryImages(imageMap);
      })
      .catch(err => console.error('Error fetching images:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carousel rotation effect
  useEffect(() => {
    if (hoveredProduct === null) return;

    const interval = setInterval(() => {
      setCurrentCarouselIndex(prev => {
        const carousel = hoveredProduct === 1 ? ezCareCarousel : 
                        hoveredProduct === 2 ? ezCarePlusCarousel : 
                        lifeBeaconCarousel;
        
        if (carousel.length === 0) return prev;

        return {
          ...prev,
          [hoveredProduct]: ((prev[hoveredProduct] || 0) + 1) % carousel.length
        };
      });
    }, 1500); // Change image every 1.5 seconds

    return () => clearInterval(interval);
  }, [hoveredProduct, ezCareCarousel, ezCarePlusCarousel, lifeBeaconCarousel]);

  // Get product images from API
  const getProductImage = (product: any) => {
    if (product && product.imageFilename) {
      const img = apiImages.find((img: any) => img.filename === product.imageFilename);
      return img ? `${API_URL}/api/images/${img.id}` : '';
    }
    return '';
  };

  const [apiImages, setApiImages] = useState<any[]>([]);

  // Fetch images for product lookup
  useEffect(() => {
    fetch(`${API_URL}/api/images`)
      .then(res => res.json())
      .then((data: any) => {
        setApiImages(data.images || []);
      })
      .catch(err => console.error('Error fetching images for products:', err));
  }, [API_URL]);

  // Medical Alert Systems - map from API products (exclude fall detection variants)
  const systems = apiProducts
    .filter(p => p.isActive && !p.shortName.includes('-fall'))
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((product, index) => {
      // Find the lowest annual plan price
      const annualPlan = product.pricingPlans?.find((p: any) => p.billingCycle === 'annual');
      const price = annualPlan?.monthlyPrice || product.basePrice || 0;
      
      // Map features for display
      let features: string[] = [];
      if (product.shortName === 'ez-care') {
        features = ['Landline', 'Fall Detection Available'];
      } else if (product.shortName === 'ez-care-plus') {
        features = ['Wireless', 'Fall Detection Available'];
      } else if (product.shortName === 'lifebeacon') {
        features = ['Phone Not Required', 'Fall Detection Available', 'Built-In Speaker'];
      }
      
      return {
        id: index + 1,
        name: product.name,
        shortName: product.shortName,
        price,
        priceType: 'FROM',
        priceUnit: '',
        category: 'system',
        image: getProductImage(product),
        features
      };
    });

  // Accessories - will be populated from API
  const [accessoriesFromAPI, setAccessoriesFromAPI] = useState<any[]>([]);

  // Fetch accessories from API
  useEffect(() => {
    const fetchAccessoriesData = async () => {
      try {
        console.log('Fetching accessories from:', `${API_URL}/api/accessories`);
        const response = await fetch(`${API_URL}/api/accessories`);
        const data = await response.json();
        console.log('Accessories API response:', data);
        console.log('Accessories count:', data.accessories?.length || 0);
        setAccessoriesFromAPI(data.accessories || []);
      } catch (error) {
        console.error('Error fetching accessories:', error);
      }
    };
    
    fetchAccessoriesData();
  }, [API_URL]);

  // Map accessories to include images - fetch images separately for accessories
  const [accessoryImageUrls, setAccessoryImageUrls] = useState<{[key: string]: string}>({});
  
  useEffect(() => {
    if (accessoriesFromAPI.length > 0) {
      fetch(`${API_URL}/api/images`)
        .then(res => res.json())
        .then((data: any) => {
          const imageMap: {[key: string]: string} = {};
          accessoriesFromAPI.forEach(acc => {
            if (acc.imageFilename) {
              const img = data.images.find((i: any) => i.filename === acc.imageFilename);
              if (img) {
                imageMap[acc.id] = `${API_URL}/api/images/${img.id}`;
              }
            }
          });
          setAccessoryImageUrls(imageMap);
        })
        .catch(err => console.error('Error fetching accessory images:', err));
    }
  }, [accessoriesFromAPI, API_URL]);

  const accessories = accessoriesFromAPI.map(acc => ({
    ...acc,
    image: accessoryImageUrls[acc.id] || '',
    features: []
  }));

  return (
    <div className="accessories-page">
      {/* Hero Section */}
      <section className="accessories-hero-section">
        <div className="accessories-hero-container">
          <h1 className="accessories-hero-title">Medical Alert System Accessories</h1>
        </div>
      </section>

      {/* Medical Alert Systems Section */}
      <section className="accessories-products-section">
        <div className="accessories-products-container">
          <h2 className="accessories-section-title">Get A Medical Alert System Seniors Love</h2>
          
          {/* Product Grid - Just the 3 main systems */}
          <div className="accessories-product-grid">
            {systems.map(product => {
              const carousel = product.id === 1 ? ezCareCarousel : 
                              product.id === 2 ? ezCarePlusCarousel : 
                              lifeBeaconCarousel;
              const isHovered = hoveredProduct === product.id;
              
              // Create full image array: product image first, then carousel images
              const allImages = product.image ? [product.image, ...carousel] : carousel;
              const carouselIndex = currentCarouselIndex[product.id] || 0;
              const displayImage = isHovered && allImages.length > 0
                ? allImages[carouselIndex % allImages.length]
                : product.image;

              return (
                <div 
                  key={product.id} 
                  className="accessories-product-card"
                  onClick={() => navigate(`/products/${product.shortName}`)}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => {
                    setHoveredProduct(product.id);
                    setCurrentCarouselIndex(prev => ({ ...prev, [product.id]: 0 }));
                  }}
                  onMouseLeave={() => {
                    setHoveredProduct(null);
                    setCurrentCarouselIndex(prev => ({ ...prev, [product.id]: 0 }));
                  }}
                >
                  <div className="accessories-product-image">
                    {displayImage ? (
                      <img src={displayImage} alt={product.name} />
                    ) : (
                      <div className="accessories-product-placeholder">
                        <svg width="200" height="150" viewBox="0 0 200 150" fill="none">
                          <rect width="200" height="150" fill="#f0f0f0"/>
                        </svg>
                      </div>
                    )}
                  </div>
                <h3 className="accessories-product-name">{product.name}</h3>
                <p className="accessories-product-price">
                  {product.priceType && <span className="price-type">{product.priceType} </span>}
                  ${product.price.toFixed(2)}
                  {product.priceUnit && <span className="price-unit">{product.priceUnit}</span>}
                </p>
                {product.features.length > 0 && (
                  <ul className="accessories-product-features">
                    {product.features.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                )}
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Accessories Section */}
      <section className="accessories-alert-systems-section">
        <div className="accessories-alert-systems-container">
          <h2 className="accessories-section-title">Try These Extras to Customize Your Medical Alert</h2>
          
          <div className="accessories-accessories-grid">
            {accessories.length > 0 ? (
              accessories.map(product => (
                <div 
                  key={product.id} 
                  className="accessories-product-card"
                  onClick={() => navigate(`/accessories/${product.shortName || product.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="accessories-product-image">
                    {product.image ? (
                      <img src={product.image} alt={product.name} />
                    ) : (
                      <div className="accessories-product-placeholder">
                        <svg width="200" height="150" viewBox="0 0 200 150" fill="none">
                          <rect width="200" height="150" fill="#f0f0f0"/>
                        </svg>
                      </div>
                    )}
                  </div>
                  <h3 className="accessories-product-name">{product.name}</h3>
                  <p className="accessories-product-price">
                    {product.priceType && <span className="price-type">{product.priceType} </span>}
                    ${product.price.toFixed(2)}
                    {product.priceUnit && <span className="price-unit">{product.priceUnit}</span>}
                  </p>
                </div>
              ))
            ) : (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem' }}>
                <p>Loading accessories... If this persists, the accessories database needs to be seeded.</p>
                <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '1rem' }}>
                  Admin: Run <code>npm run seed:accessories</code> on Railway
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Comprehensive Guide Section */}
      <section className="accessories-guide-section">
        <div className="accessories-guide-container">
          <div className="accessories-guide-content">
            <div className="accessories-guide-image">
              {guideImage ? (
                <img src={guideImage} alt="Senior with medical alert" className="accessories-guide-image-placeholder" />
              ) : (
                <div className="accessories-guide-image-placeholder">
                  <svg width="400" height="400" viewBox="0 0 400 400" fill="none">
                    <rect width="400" height="400" fill="#f0f0f0" rx="8"/>
                  </svg>
                </div>
              )}
            </div>

            <div className="accessories-guide-text">
              <h2 className="accessories-guide-title">Medical Alert Necklaces: A Comprehensive Guide</h2>
              
              <p className="accessories-guide-paragraph">
                Medical alert necklaces cover categories that serve older adults. When discussing medical alert necklaces, people refer to either <strong>medical alert jewelry</strong> that displays critical health information or wearable <strong>medical alert devices</strong> that can summon emergency assistance.
              </p>
              
              <p className="accessories-guide-paragraph">
                Both products are for senior safety and in managing medical emergencies.
              </p>

              <h3 className="accessories-guide-subtitle">Medical ID Necklaces: Passive Protection</h3>
              
              <p className="accessories-guide-paragraph">
                Medical ID necklaces are medical alert jewelry that functions similarly to military identification tags. These medical necklaces contain engraved or embedded information about the wearer's specific medical conditions, allergies, medications, and emergency contacts.
              </p>
              
              <p className="accessories-guide-paragraph">
                For seniors with chronic conditions, cognitive impairments, or communication difficulties, medical jewelry pieces are silent advocates during a medical emergency.
              </p>

              <h4 className="accessories-guide-subheading">Features and benefits:</h4>
              
              <ul className="accessories-guide-list">
                <li>No monthly fees or activation fees</li>
                <li>Available in various materials including stainless steel, sterling silver, and rose gold</li>
                <li>Waterproof and durable for continuous wear</li>
                <li>No batteries or charging required</li>
                <li>Immediately visible to emergency services personnel</li>
                <li>Can be customized with different medical conditions and personal information</li>
                <li>More fashionable options blend medical jewelry with attractive designs</li>
              </ul>
              
              <p className="accessories-guide-paragraph">
                Unlike electronic medical alert devices, these identifiers work even when unconscious and require no action from the wearer to provide vital information to first responders.
              </p>

              <h3 className="accessories-guide-subtitle">Medical Alert System Necklaces: Active Protection</h3>
              
              <p className="accessories-guide-paragraph">
                The second category refers to wearable emergency call systems that allow users to contact emergency services with the push of a button. These medical alert devices are particularly valuable for seniors living independently who face fall risks or other sudden medical emergencies.
              </p>
              
              <p className="accessories-guide-paragraph">Popular providers include:</p>
              
              <ul className="accessories-guide-list">
                <li>Life Alert, known for their "I've fallen and can't get up" commercials</li>
                <li>Alert-1, known for low cost, high value plans</li>
                <li>Medical Guardian, offering multiple tiers of protection</li>
                <li>Bay Alarm Medical, providing GPS-enabled options</li>
                <li>Other services focusing on senior safety and independence</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Functionality Section */}
      <section className="accessories-functionality-section">
        <div className="accessories-functionality-container">
          <h3 className="accessories-functionality-title">Functionality:</h3>
          
          <ul className="accessories-functionality-list">
            <li>Button-activated pendants worn as necklaces</li>
            <li>Direct connection to monitoring centers that dispatch emergency services</li>
            <li>Two-way communication capabilities through the pendant or base station</li>
            <li>Fall detection technology in premium models</li>
            <li>GPS location tracking in mobile medical alert versions</li>
            <li>More reliable than depending on a cell phone which may be out of reach during a crisis</li>
          </ul>
          
          <p className="accessories-functionality-paragraph">
            When activated during a medical emergency, these systems contact professional monitoring centers and alert emergency contacts and family members so seniors receive fast medical attention and loved ones stay informed.
          </p>
          
          <p className="accessories-functionality-paragraph">
            Most medical alert systems require monthly subscription fees ranging from $19-$49 depending on features, with some companies charging initial activation fees. Despite these ongoing costs, many families consider these devices essential investments in senior safety.
          </p>

          <h3 className="accessories-functionality-subtitle">Choosing Between Medical ID and Alert Systems</h3>
          
          <p className="accessories-functionality-paragraph">
            Many seniors and their caregivers <strong>choose to use both</strong> types of medical necklaces:
          </p>
          
          <ul className="accessories-functionality-list">
            <li>Medical ID jewelry for passive, always-available medical information</li>
            <li>Medical alert systems for active emergency response capabilities</li>
          </ul>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AccessoriesPage;

